import { useParams } from "react-router-dom";
import { useClientScope } from "@/components/pages/Client/settings/ClientScopeContext";
import { useAppSelector } from "@/redux/hooks";
import { activeOperatorSelector, bookableCountriesSelector } from "@/redux/slices/operator/selectors";

/**
 * The countries the booking sheet may place a trip in — applied to the typed address search, the saved
 * favourites/recents/provider POIs it offers, the pin-on-map result, and the airport-terminal lookup that
 * follows a flight pickup (DEV-1606, DEV-1612, DCS-367).
 *
 * - Everyone gets the provider's bookable list, `bookable_country_list` (the domain's
 *   `d-operates-in-countries`, AU and NZ for Royale): riders, guests, EAs on behalf, and operators on
 *   behalf alike. Adding a country there is how a provider widens where its riders may book.
 * - `null` — no restriction at all — for exactly one case: an operator booking on behalf of a client
 *   (the Clients → Book route, which carries `:operatorId` and a managed-client scope) whose provider has
 *   the hidden "Worldwide Address Search" add-on switched on (`add_ons.worldwide_address_search`, a PHP
 *   add-on Fleet Actions enables per provider). The legacy sheet lifted its restriction for staff once
 *   the Fixed International trip type was picked; this sheet asks for the address first, so the lift keys
 *   off who is booking and what their provider has bought.
 *
 * An empty list means the provider has no country data and nothing is offered. Same operator test as
 * PricedBookingSummary's `isOperatorScoped`, so the price override and the wider search go to the same
 * person. Nothing server-side reads this: the quote engine's country whitelist and the admin-only
 * transfer-type gate still apply to whatever address gets through.
 */
export const useAddressSearchCountries = (): string[] | null => {
  const { accountId } = useClientScope();
  const { operatorId } = useParams<{ operatorId: string }>();
  const bookableCountries = useAppSelector(bookableCountriesSelector);
  const worldwideAddOn = useAppSelector(activeOperatorSelector)?.addOns?.worldwideAddressSearch ?? false;
  const operatorOnBehalf = Boolean(accountId && operatorId);
  return operatorOnBehalf && worldwideAddOn ? null : bookableCountries;
};
